import React, { useState, useEffect } from 'react';
import { dashboardService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import ReportsTable from '../components/ReportsTable';
import ReportDetailsModal from '../components/ReportDetailsModal';
import FileViewer from '../components/FileViewer';
import AnalyticsCharts from '../components/AnalyticsCharts';

// Fallback mock data (will only be used if API fails)
const mockReports = [
  {
    id: 1,
    reference_id: 'REF-8X72YFGH',
    contractor_name: 'ABC Construction Inc.',
    project_location: 'Makati City, Manila',
    created_at: '2025-08-15',
    status: 'pending'
  },
  {
    id: 2,
    reference_id: 'REF-9Z45KLMP',
    contractor_name: 'Metro Builders Co.',
    project_location: 'Quezon City, Manila',
    created_at: '2025-08-10',
    status: 'under_investigation'
  },
  {
    id: 3,
    reference_id: 'REF-7H23QRST',
    contractor_name: 'Philippine Developers Ltd.',
    project_location: 'Cebu City',
    created_at: '2025-08-05',
    status: 'resolved'
  },
  {
    id: 4,
    reference_id: 'REF-6J91UVWX',
    contractor_name: 'Island Construction Group',
    project_location: 'Davao City',
    created_at: '2025-07-30',
    status: 'under_investigation'
  },
  {
    id: 5,
    reference_id: 'REF-5L67MNOP',
    contractor_name: 'National Infrastructure Corp.',
    project_location: 'Baguio City',
    created_at: '2025-07-25',
    status: 'pending'
  }
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // File viewer state
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);

  // Fetch reports function
  const fetchDashboardData = async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch reports
      const reportsResponse = await dashboardService.getReports();

      // Handle reports response
      if (reportsResponse.data?.success) {
        setReports(reportsResponse.data.data?.data || []);
        setLastUpdated(new Date());
      } else {
        throw new Error('Invalid reports response format');
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);

      if (err.response?.status === 401 && !isAutoRefresh) {
        // Authentication failed, logout and redirect to login
        await logout();
        navigate('/login');
        return;
      }

      if (!isAutoRefresh) {
        setError('Failed to load dashboard data. Using mock data instead.');
        setReports(mockReports);
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Initial fetch
    fetchDashboardData();
  }, [navigate, isAuthenticated, logout]);

  // Real-time polling every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(true); // Auto-refresh
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Manual refresh function
  const handleRefresh = () => {
    fetchDashboardData();
  };

  // File viewer handler
  const handleFileView = (file) => {
    setSelectedFile(file);
    setIsFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setIsFileViewerOpen(false);
    setSelectedFile(null);
  };

  // Filter reports based on status, search term, and date range
  const filteredReports = reports.filter(report => {
    const matchesStatus = filterStatus === 'All' || report.status === filterStatus;
    const matchesSearch = !searchTerm ||
      report.reference_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.contractor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.project_location.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDate = true;
    if (dateRange !== 'all') {
      const reportDate = new Date(report.created_at);
      const now = new Date();
      const daysAgo = {
        '7': 7,
        '30': 30,
        '90': 90
      }[dateRange];

      if (daysAgo) {
        const cutoffDate = new Date(now - daysAgo * 24 * 60 * 60 * 1000);
        matchesDate = reportDate >= cutoffDate;
      }
    }

    return matchesStatus && matchesSearch && matchesDate;
  });

  const handleUpdateStatus = async (reportId, newStatus, notes) => {
    try {
      await dashboardService.updateReportStatus(reportId, newStatus, notes);

      // Update the reports list with the new status
      setReports(reports.map(r =>
        r.id === reportId ? {...r, status: newStatus} : r
      ));

      // Update the selected report if it's the one being changed
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport({...selectedReport, status: newStatus});
      }

      // Show success notification
      if (window.showToast) {
        window.showToast(`Report status updated to "${newStatus}"`, 'success');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      if (window.showToast) {
        window.showToast('Failed to update status. Please try again.', 'error');
      }
      throw error; // Re-throw to be handled by the modal
    }
  };

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setIsDetailsModalOpen(true);
  };

  // Calculate statistics from current data
  const getStatistics = () => {
    const totalReports = reports.length;
    const pendingReports = reports.filter(r => r.status === 'pending').length;
    const underInvestigation = reports.filter(r => r.status === 'under_investigation').length;
    const resolved = reports.filter(r => r.status === 'resolved').length;
    const dismissed = reports.filter(r => r.status === 'dismissed').length;

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    // This month vs last month comparison
    const thisMonth = reports.filter(r => {
      const reportDate = new Date(r.created_at);
      return reportDate.getMonth() === currentMonth && reportDate.getFullYear() === currentYear;
    }).length;

    const lastMonthReports = reports.filter(r => {
      const reportDate = new Date(r.created_at);
      return reportDate.getMonth() === lastMonth && reportDate.getFullYear() === lastMonthYear;
    }).length;

    // Calculate trends based on last 30 days vs previous 30 days
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const last60Days = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    const reportsLast30Days = reports.filter(r => new Date(r.created_at) >= last30Days);
    const reportsPrevious30Days = reports.filter(r => {
      const date = new Date(r.created_at);
      return date >= last60Days && date < last30Days;
    });

    const calculateTrend = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : null;
      return parseFloat(((current - previous) / previous * 100).toFixed(1));
    };

    const totalTrend = calculateTrend(reportsLast30Days.length, reportsPrevious30Days.length);
    const pendingTrend = calculateTrend(
      reportsLast30Days.filter(r => r.status === 'pending').length,
      reportsPrevious30Days.filter(r => r.status === 'pending').length
    );
    const investigationTrend = calculateTrend(
      reportsLast30Days.filter(r => r.status === 'under_investigation').length,
      reportsPrevious30Days.filter(r => r.status === 'under_investigation').length
    );
    const resolvedTrend = calculateTrend(
      reportsLast30Days.filter(r => r.status === 'resolved').length,
      reportsPrevious30Days.filter(r => r.status === 'resolved').length
    );
    const thisMonthTrend = calculateTrend(thisMonth, lastMonthReports);

    const resolutionRate = totalReports > 0 ? ((resolved / totalReports) * 100).toFixed(1) : 0;

    // Calculate average response time from actual data
    const resolvedReports = reports.filter(r => r.status === 'resolved');
    let avgResponseTime = '0';
    if (resolvedReports.length > 0) {
      const totalDays = resolvedReports.reduce((sum, report) => {
        const created = new Date(report.created_at);
        const updated = new Date(report.updated_at);
        return sum + Math.ceil((updated - created) / (1000 * 60 * 60 * 24));
      }, 0);
      avgResponseTime = (totalDays / resolvedReports.length).toFixed(1);
    }

    return {
      totalReports,
      pendingReports,
      underInvestigation,
      resolved,
      dismissed,
      thisMonth,
      resolutionRate,
      avgResponseTime,
      trends: {
        totalTrend,
        pendingTrend,
        investigationTrend,
        resolvedTrend,
        thisMonthTrend
      }
    };
  };

  const stats = getStatistics();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary mb-1">Government Dashboard</h1>
            <p className="text-gray-600 text-sm">
              Monitor and manage all submitted reports efficiently.
            </p>
          </div>
          <div className="flex flex-col items-end space-y-2">
            <button
              onClick={handleRefresh}
              disabled={loading || isRefreshing}
              className="btn-primary text-sm px-3 py-1"
            >
              {loading || isRefreshing ? '🔄' : '↻'} Refresh
            </button>
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards - Compact Layout */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <StatCard
          title="Total Reports"
          value={stats.totalReports}
          subtitle="All time"
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/></svg>}
          color="blue"
          trend={stats.trends.totalTrend}
          compact={true}
        />
        <StatCard
          title="Pending"
          value={stats.pendingReports}
          subtitle="Requiring attention"
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/></svg>}
          color="blue"
          trend={stats.trends.pendingTrend}
          compact={true}
        />
        <StatCard
          title="Under Investigation"
          value={stats.underInvestigation}
          subtitle="In progress"
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
          color="yellow"
          trend={stats.trends.investigationTrend}
          compact={true}
        />
        <StatCard
          title="Resolved"
          value={stats.resolved}
          subtitle={`${stats.resolutionRate}% rate`}
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>}
          color="green"
          trend={stats.trends.resolvedTrend}
          compact={true}
        />
        <StatCard
          title="This Month"
          value={stats.thisMonth}
          subtitle="Reports submitted"
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/></svg>}
          color="blue"
          trend={stats.trends.thisMonthTrend}
          compact={true}
        />
        <StatCard
          title="Avg Response"
          value={`${stats.avgResponseTime}d`}
          subtitle="Response time"
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>}
          color="gray"
          compact={true}
        />
        <StatCard
          title="Dismissed"
          value={stats.dismissed}
          subtitle="Invalid/duplicate"
          icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>}
          color="red"
          compact={true}
        />
      </div>

      {/* Analytics Toggle */}
      <div className="bg-white p-3 rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-primary">Analytics Dashboard</h2>
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="btn-secondary text-sm px-3 py-1"
          >
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
        </div>

        {showAnalytics && (
          <div className="mt-4 animate-fade-in">
            <AnalyticsCharts reports={reports} />
          </div>
        )}
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <h2 className="text-lg font-bold text-primary mb-3">Filter Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="form-label">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by reference ID, contractor, or location..."
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-input"
            >
              <option value="All">All Reports</option>
              <option value="new">New</option>
              <option value="pending">Pending</option>
              <option value="under_investigation">Under Investigation</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
          <div>
            <label className="form-label">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="form-input"
            >
              <option value="all">All Time</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-gray-600 text-sm">
            Showing {filteredReports.length} of {reports.length} reports
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('All');
                setDateRange('all');
              }}
              className="border border-gray-300 text-gray-700 px-3 py-1 text-sm rounded hover:bg-gray-50"
            >
              Clear Filters
            </button>
            <button
              onClick={async () => {
                try {
                  const response = await fetch('/api/dashboard/export', {
                    method: 'GET',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('ncrs_token')}`
                    }
                  });

                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'reports_export.csv';
                    a.click();
                    window.URL.revokeObjectURL(url);

                    if (window.showToast) {
                      window.showToast('Reports exported successfully!', 'success');
                    }
                  } else {
                    throw new Error('Export failed');
                  }
                } catch (error) {
                  console.error('Error exporting reports:', error);
                  if (window.showToast) {
                    window.showToast('Failed to export reports', 'error');
                  }
                }
              }}
              className="btn-primary text-sm px-3 py-1"
            >
              Export All
            </button>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <ReportsTable
        reports={filteredReports}
        loading={loading}
        error={error}
        onViewDetails={handleViewDetails}
      />

      {/* Report Details Modal */}
      <ReportDetailsModal
        report={selectedReport}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedReport(null);
        }}
        onStatusUpdate={handleUpdateStatus}
        onFileView={handleFileView}
      />

      {/* File Viewer Modal - Outside of Report Details Modal */}
      <FileViewer
        file={selectedFile}
        isOpen={isFileViewerOpen}
        onClose={handleCloseFileViewer}
      />
    </div>
  );
};

export default DashboardPage;
